import React from "react";
import { FiChevronRight, FiHome, FiLayers, FiMapPin } from "react-icons/fi";

import { useAdminContext } from "../../context/admin/AdminContext";
import { getOneLevel } from "../../lib/breadCrumb/BreadCrumb";
import { countriesList } from "./data";

/**
 * Resolve the country to show in the breadcrumb/chips.
 *
 * Original behavior:
 *  1. If there's a current level selected, use its country_id to pick one
 *     from `countriesList`.
 *  2. Otherwise fall back to `sysAdmin.country`.
 *  3. Callers could also pre-seed an array via `props.country` — most don't.
 *
 * The old implementation mutated the passed-in array with `.push(...)` and
 * then tried to render that array directly as a React child (which throws
 * "Objects are not valid as a React child" once anything is pushed). We
 * now compute a display string instead — same resolution logic, no mutation.
 */
const resolveCountry = ({ seed, level, sysAdminCountry }) => {
  if (seed.length > 0) return seed[0];
  if (level.length > 0 && level[0]?.name !== "") {
    const country_id = level[0]?.data?.data?.country_id;
    return countriesList.find((c) => c?.id === country_id) ?? null;
  }
  return countriesList.find((c) => c?.name === sysAdminCountry) ?? null;
};

export const DashboardIntro = ({ props }) => {
  const { sysAdmin } = useAdminContext();
  const { name, country = [] } = props || {};
  const level = getOneLevel() ?? [];

  const selectedCountry = resolveCountry({
    seed: Array.isArray(country) ? country : [],
    level,
    sysAdminCountry: sysAdmin?.country,
  });

  const countryName = selectedCountry?.name ?? "-";
  const levelName =
    level.length > 0 && level[0]?.name !== "" ? level[0]?.name : null;
  const showIntro = name !== "undefined undefined";

  if (!showIntro) return null;

  return (
    <section className="w-full">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-3 sm:mb-4">
            <h2 className="text-base font-semibold tracking-tight text-gray-900 sm:text-lg">
              <span className="capitalize">Company</span>
              <span className="ml-2 text-gray-400">•</span>
              <span className="ml-2 capitalize text-gray-700">
                {name ?? "-"}
                {levelName ? ` / ${levelName}` : ""}
              </span>
            </h2>
            <p className="mt-1 text-xs text-gray-600">
              You’re viewing data scoped to the selected location and level.
              Use the filters above to change context.
            </p>
          </div>

          <nav aria-label="Breadcrumb" className="mb-3">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-gray-600">
              <li className="inline-flex items-center gap-2">
                <FiHome className="h-4 w-4 text-gray-500" aria-hidden="true" />
                <span className="font-medium text-gray-700">Dashboard</span>
              </li>

              <li className="inline-flex items-center gap-2">
                <FiChevronRight className="h-4 w-4 text-gray-400" />
                <span className="inline-flex items-center gap-2">
                  <FiMapPin
                    className="h-4 w-4 text-emerald-600"
                    aria-hidden="true"
                  />
                  <span className="text-gray-800">{countryName}</span>
                </span>
              </li>

              {levelName ? (
                <li className="inline-flex items-center gap-2">
                  <FiChevronRight className="h-4 w-4 text-gray-400" />
                  <span className="inline-flex items-center gap-2">
                    <FiLayers
                      className="h-4 w-4 text-emerald-600"
                      aria-hidden="true"
                    />
                    <span className="text-gray-800">{levelName}</span>
                  </span>
                </li>
              ) : null}
            </ol>
          </nav>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700"
              title="Selected country"
            >
              <FiMapPin className="h-3.5 w-3.5 text-emerald-600" />
              <span className="font-medium">{countryName}</span>
            </span>

            {levelName ? (
              <span
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700"
                title="Selected level"
              >
                <FiLayers className="h-3.5 w-3.5 text-emerald-600" />
                <span className="font-medium">{levelName}</span>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};
